import { useState, useRef, useLayoutEffect } from 'react';
import { useEntityPresentation } from '@backstage/plugin-catalog-react';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';

// ── Colour palette ────────────────────────────────────────────────────────────

export const KIND_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  domain:    { fill: '#5C6BC0', stroke: '#3949AB', text: '#fff' },
  system:    { fill: '#00897B', stroke: '#00695C', text: '#fff' },
  component: { fill: '#1E88E5', stroke: '#1565C0', text: '#fff' },
  resource:  { fill: '#FB8C00', stroke: '#E65100', text: '#fff' },
  api:       { fill: '#43A047', stroke: '#2E7D32', text: '#fff' },
  group:     { fill: '#8D6E63', stroke: '#6D4C41', text: '#fff' },
  user:      { fill: '#7E57C2', stroke: '#512DA8', text: '#fff' },
};

const DEFAULT_PALETTE = { fill: '#607D8B', stroke: '#455A64', text: '#fff' };

export function kindPalette(kind: string) {
  return KIND_COLORS[kind.toLowerCase()] ?? DEFAULT_PALETTE;
}

// ── Custom node renderer ───────────────────────────────────────────────────────

export function CatalogGraphNode({ node, dimmed }: { node: { entity?: { kind?: string; metadata?: { name?: string; namespace?: string }; spec?: Record<string, unknown> }; focused?: boolean; color?: string; id?: string; onClick?: (...args: any[]) => void }; dimmed?: boolean }) {
  const { id, entity, focused, onClick } = node as any;
  const palette = kindPalette(entity?.kind ?? '');

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const textRef = useRef<SVGTextElement>(null);

  const presentation = useEntityPresentation(entity, { defaultNamespace: DEFAULT_NAMESPACE });

  useLayoutEffect(() => {
    if (textRef.current) {
      const bb = textRef.current.getBBox();
      const rh = Math.round(bb.height);
      const rw = Math.round(bb.width);
      if (rh !== height || rw !== width) {
        setWidth(rw);
        setHeight(rh);
      }
    }
  }, [width, height]);

  const padding = 10;
  const paddedWidth = width + padding * 2;
  const paddedHeight = height + padding * 2;
  const displayTitle = presentation.primaryTitle ?? id;
  const opacity = dimmed ? 0.35 : 1;

  return (
    <g onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined, opacity }}>
      <rect
        width={paddedWidth}
        height={paddedHeight}
        rx={8}
        style={{ fill: palette.fill, stroke: palette.stroke }}
        strokeWidth={focused ? 2.5 : 1.5}
      />
      <text
        ref={textRef}
        style={{ fill: palette.text, fontSize: 12, fontFamily: 'sans-serif', fontWeight: focused ? 'bold' : 'normal' }}
        x={paddedWidth / 2}
        y={paddedHeight / 2}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {displayTitle}
      </text>
      <title>{presentation.entityRef}</title>
    </g>
  );
}
